import { Component, OnDestroy, OnInit } from "@angular/core";
import { AbstractControl, UntypedFormBuilder, Validators } from "@angular/forms";
import { forkJoin, Subscription } from "rxjs";
import { distinctUntilChanged, map, mergeMap, switchMap, tap } from "rxjs/operators";
import { CountryService, CultureService, CurrencyService, FiscalSetupService, IActiveTerms, ICountry, ICulture, ICurrency, IFiscalSetup, IFiscalSetupProvider, ProviderService, TermsCultureService, TermsHandlingService } from "src/app/xena-api";
import { stringPropertyIgnoreCase } from "src/app/utils/comparers";
import { CountryCodeToCurrency } from "src/app/utils/countrycode-to-currency";
import { TimezoneToCountryCodes } from "src/app/utils/timezone-to-countrycode";
import { environment } from "src/environments/environment";

@Component({
    selector: 'app-create-fiscal',
    templateUrl: './create-fiscal.component.html',
    standalone: false
})
export class CreateFiscalComponent implements OnInit, OnDestroy {
  private subscriptions: Subscription[] = [];

  showError = false;
  open: boolean = true;
  creating: boolean = false;
  currentStep: number = 1;
  countries: ICountry[] = [];
  currencies: ICurrency[] = [];
  cultures: ICulture[] = [];
  providers: IFiscalSetupProvider[] = [];
  terms: IActiveTerms[] = [];
  fiscal: IFiscalSetup;

  form = this.fb.group({
    fiscalSetup: this.fb.group({
      orgNumber: ['', Validators.required],
      danishCompany: null,
      registeredForVat: [true],
      address: this.fb.group({
        name: ['', Validators.required],
        street: [''],
        placeName: [''],
        zip: [''],
        city: [''],
        countryName: [TimezoneToCountryCodes.guess(environment.defaults.country)[0]],
      }),
      phone: ['', Validators.required],
      email: ['', [Validators.required, Validators.email]],
      currencyAbbreviation: [],
      culture: [],
      providerId: []
    }),
    terms: this.fb.group({
      acceptedTerms: [[]]
    })
  });

  get fiscalSetupGroup(): AbstractControl {
    return this.form.get('fiscalSetup');
  }

  get termsGroup(): AbstractControl {
    return this.form.get('terms');
  }

  constructor(
    private fb: UntypedFormBuilder,
    private countryService: CountryService,
    private currencyService: CurrencyService,
    private cultureService: CultureService,
    private termsHandlingService: TermsHandlingService,
    private termsCultureService: TermsCultureService,
    private fiscalSetupService: FiscalSetupService,
    private providerService: ProviderService,
  ) { }

  ngOnInit(): void {
    this.subscriptions = [
      this.fiscalSetupGroup.get('providerId').valueChanges.pipe(
        distinctUntilChanged(),
        switchMap(providerId => this.termsHandlingService.list({ forceNoPaging: true, providerId })),
        map(data => data.entities))
        .subscribe(t => this.terms = t),
      this.countryService.list({ forceNoPaging: true }).pipe(
        map(data => data.entities.sort(stringPropertyIgnoreCase('translatedName'))))
        .subscribe(c => this.countries = c),
      this.currencyService.list({ forceNoPaging: true }).pipe(
        map(data => data.entities.sort(stringPropertyIgnoreCase('currencyDisplayName'))))
        .subscribe(c => this.currencies = c),
      this.cultureService.list({ forceNoPaging: true }).pipe(
        map(data => data.entities.sort(stringPropertyIgnoreCase('name'))))
        .subscribe(c => this.cultures = c),
      this.providerService.list({ forceNoPaging: true }).pipe(
        map(data => data.entities),
        tap(data => this.fiscalSetupGroup.get('providerId').setValue(data.map(p => p.fiscalSetupId)[0])),
      )
        .subscribe(p => this.providers = p)
    ];
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach(subscription => {
      subscription.unsubscribe();
    });
  }

  setDefaults(): void {
    const country = this.fiscalSetupGroup.get('address.countryName').value;
    this.fiscalSetupGroup.patchValue({
      currencyAbbreviation: CountryCodeToCurrency.convert(country) || environment.defaults.currency,
      culture: this.cultures.map(c => c.culture).find(c => c.toUpperCase().endsWith(country?.toUpperCase())) || environment.defaults.culture
    });
  }

  setFiscal(): void {
    const fiscal = this.fiscalSetupGroup.value;
    const address = fiscal.address;

    this.fiscal = {
      ...fiscal,
      address: {
        ...address,
        countryDisplayName: this.countries.find(c => c.abbreviation === address.countryName)?.translatedName || null
      },
      currencyDescription: this.currencies.find(c => c.iSOCurrencySymbol === fiscal.currencyAbbreviation)?.currencyDisplayName || null,
      cultureDisplayName: this.cultures.find(c => c.culture === fiscal.culture)?.name || null,
      providerName: this.providers.find(p => p.fiscalSetupId == fiscal.providerId)?.onboardingTypeNameTranslated || null,
    };
  }

  cancel() {
    this.open = false;
  }

  back() {
    this.currentStep--;
  }

  next() {
    switch (this.currentStep) {
      case 1:
        if (this.fiscalSetupGroup.invalid) {
          this.fiscalSetupGroup.markAllAsTouched();
          return;
        }
        this.setDefaults();
        break;
      case 2:
        this.setFiscal();
        break;
      default:
        return;
    }
    this.currentStep++;
  }

  create() {
    if (this.form.invalid) {
      this.showError = true;
      return;
    }

    this.creating = true;
    const terms = this.termsGroup.get('acceptedTerms').value;

    this.fiscalSetupService.create(this.fiscal).pipe(
      mergeMap(fiscalSetup => {
        const requests = terms.map(termId => this.termsCultureService.saveAcceptedTerms(termId, { fiscalSetupId: fiscalSetup.id }));
        return forkJoin(requests).pipe(map(() => fiscalSetup.id));
      })
    ).subscribe(fiscalSetupId =>
      this.reload(fiscalSetupId)
    );
  }

  /* istanbul ignore next */
  reload(fiscalSetupId: number) { // Only way to make reload testable.
    location.pathname = `/fiscal/${fiscalSetupId}/home`;
  }
}
